import React, { useState } from 'react';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { Phone, MessageCircle, Sparkles } from 'lucide-react';
import { SEO, LocalBusinessSchema } from './SEO';
import { GoogleAnalytics, GoogleTagManager } from './GoogleAnalytics';
import { CookieConsent } from './CookieConsent';
import logoFull from 'figma:asset/fce628fa1ff2c0ce8e1c3b074b0a7732c9200683.png';

type Language = 'he' | 'en' | 'ru';

interface LandingTranslations {
  headline: string;
  subHeadline: string;
  cta: string;
  ctaWhatsApp: string;
  or: string;
  happyPets: string;
}

const translations: Record<Language, LandingTranslations> = {
  he: {
    headline: 'ShowGroom',
    subHeadline: 'סלון יופי לכלבים וחתולים 🐕🐈',
    cta: '📞 התקשרו עכשיו',
    ctaWhatsApp: '💬 שלחו וואטסאפ',
    or: 'או',
    happyPets: '✨ חיות מאושרות ✨',
  },
  en: {
    headline: 'ShowGroom',
    subHeadline: 'Pet grooming salon 🐕🐈',
    cta: '📞 Call Now',
    ctaWhatsApp: '💬 WhatsApp Us',
    or: 'or',
    happyPets: '✨ Happy Pets ✨',
  },
  ru: {
    headline: 'ShowGroom',
    subHeadline: 'Салон красоты для питомцев 🐕🐈',
    cta: '📞 Позвонить',
    ctaWhatsApp: '💬 WhatsApp',
    or: 'или',
    happyPets: '✨ Счастливые питомцы ✨',
  },
};

const getWhatsAppMessage = (lang: Language) => {
  const messages = {
    he: 'היי! אשמח לקבוע תור לטיפוח 🐕',
    en: 'Hi! I would like to book a grooming appointment 🐕',
    ru: 'Привет! Хочу записаться на груминг 🐕',
  };
  return encodeURIComponent(messages[lang]);
};

export function LandingPage() {
  const [language, setLanguage] = useState<Language>('he');
  const t = translations[language];
  const isRTL = language === 'he';

  const whatsAppLink = `https://wa.me/972555171717?text=${getWhatsAppMessage(language)}`;

  // SEO title and description based on language
  const seoContent = {
    he: {
      title: 'טיפוח כלבים וחתולים | ShowGroom - מספרת כלבים בזכרון יעקב',
      description: 'טיפוח מקצועי לכלבים וחתולים ב-ShowGroom זכרון יעקב. שירות מקצועי ואוהב. התקשרו: 077-2200052',
    },
    en: {
      title: 'Pet Grooming | ShowGroom - Pet Grooming Zichron Yaakov',
      description: 'Professional pet grooming at ShowGroom Zichron Yaakov. Professional & loving care for dogs & cats. Call: 077-2200052',
    },
    ru: {
      title: 'Груминг для питомцев | ShowGroom - Зихрон-Яаков',
      description: 'Профессиональный груминг в ShowGroom. Профессиональный уход для собак и кошек. Звоните: 077-2200052',
    },
  };

  return (
    <div
      dir={isRTL ? 'rtl' : 'ltr'}
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: '#F0C2B5' }}
    >
      {/* SEO Components */}
      <SEO
        title={seoContent[language].title}
        description={seoContent[language].description}
        canonical="https://showgroom.co.il/land1"
        ogImage="https://showgroom.co.il/images/showgroom-og.png"
        ogImageAlt="ShowGroom - מספרת כלבים וחתולים בזכרון יעקב"
      />
      <LocalBusinessSchema />
      
      {/* Analytics */}
      <GoogleAnalytics measurementId="G-2WY16JV3LW" />
      <GoogleTagManager containerId="GTM-NRSLWWMN" />

      {/* Language Selector - Top Right */}
      <div className="absolute top-4 right-4 md:top-6 md:right-6 z-50 flex gap-2">
        {(['he', 'en', 'ru'] as Language[]).map((lang) => (
          <button
            key={lang}
            onClick={() => setLanguage(lang)}
            className={`w-10 h-10 md:w-12 md:h-12 rounded-full text-sm md:text-base font-medium transition-all ${
              language === lang
                ? 'bg-white shadow-lg'
                : 'bg-white/40 hover:bg-white/60'
            }`}
            style={{ color: '#444444' }}
          >
            {lang === 'he' ? 'עב' : lang === 'en' ? 'EN' : 'РУ'}
          </button>
        ))}
      </div>

      {/* Main Content - Centered Vertically */}
      <main className="flex-1 flex items-center justify-center p-6 md:p-12">
        <div className="text-center w-full max-w-sm md:max-w-lg lg:max-w-xl">
          {/* Logo - Clickable to Main Page */}
          <a 
            href="/"
            className="block w-24 h-24 md:w-36 md:h-36 lg:w-44 lg:h-44 mx-auto mb-4 md:mb-6 transition-transform hover:scale-110"
            title="Back to ShowGroom"
          >
            <ImageWithFallback
              src={logoFull}
              alt="ShowGroom - מספרת כלבים וחתולים בזכרון יעקב"
              className="w-full h-full object-contain"
            />
          </a>

          {/* Brand Name - Also Clickable */}
          <a href="/">
            <h1
              className="text-3xl md:text-5xl lg:text-6xl mb-2 md:mb-3 hover:opacity-80 transition-opacity"
              style={{
                color: '#444444',
                fontFamily: "'Handlee', cursive",
                fontWeight: '400',
              }}
            >
              {t.headline}
            </h1>
          </a>
          
          {/* Subtitle with pet emojis */}
          <p className="text-base md:text-xl lg:text-2xl mb-3 md:mb-4" style={{ color: '#444444' }}>
            {t.subHeadline}
          </p>
          
          {/* Happy Pets Badge */}
          <div 
            className="inline-flex items-center gap-1 md:gap-2 px-4 py-2 md:px-6 md:py-3 rounded-full text-sm md:text-base mb-4 md:mb-6 animate-pulse"
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.6)', color: '#444444' }}
          >
            <Sparkles size={16} className="md:w-5 md:h-5" />
            <span>{t.happyPets}</span>
          </div>

          {/* CTA Buttons - Responsive */}
          <div className="space-y-4 md:space-y-5">
            <a
              href="tel:+972772200052"
              className="flex items-center justify-center gap-2 w-full py-4 md:py-5 lg:py-6 rounded-full shadow-lg hover:shadow-xl transition-all transform hover:scale-105 text-lg md:text-xl lg:text-2xl font-semibold"
              style={{ backgroundColor: '#444444', color: 'white' }}
            >
              {t.cta}
            </a>
            
            <div className="text-base md:text-lg" style={{ color: '#444444' }}>{t.or}</div>
            
            <a
              href={whatsAppLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-4 md:py-5 lg:py-6 rounded-full shadow-lg hover:shadow-xl transition-all transform hover:scale-105 text-lg md:text-xl lg:text-2xl font-semibold"
              style={{ backgroundColor: '#25D366', color: 'white' }}
            >
              {t.ctaWhatsApp}
            </a>
          </div>

          {/* Phone Number with love */}
          <p className="mt-5 md:mt-8 text-base md:text-xl" style={{ color: '#444444' }}>
            ❤️ 077-2200052 ❤️
          </p>
          
          {/* Address for SEO */}
          <p className="mt-2 text-sm md:text-base" style={{ color: '#8A7A74' }}>
            המייסדים 51, זכרון יעקב
          </p>
        </div>
      </main>

      {/* Cookie Consent Banner */}
      <CookieConsent language={language} />
    </div>
  );
}
